'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';

import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { CategoryProductsSwiper } from '../shared/category-products-swiper';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  swiper?: boolean;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filters = useCategoryStore(s => s.filter);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = searchParams.get('page');

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = useMemo(
    () =>
      buildProductsQuery({
        categories: [props.category.id],
        manufacturers: filters.manufacturers ?? [],
        results: generalLayout?.pagination?.paginationProducts || 12,
        order: filters.sort.order,
        orderBy: filters.sort.orderBy,
        attributes: filters.attributes,
        price_from: filters.price_min,
        price_to: filters.price_max,
        availabilities: filters.availabilities,
        suppliers: filters.suppliers,
      }),
    [
      filters.attributes,
      filters.availabilities,
      filters.manufacturers,
      filters.price_max,
      filters.price_min,
      filters.sort.order,
      filters.sort.orderBy,
      filters.suppliers,
      generalLayout?.pagination?.paginationProducts,
      props.category.id,
    ],
  );

  const productRequestUri = buildProductsRequest({
    ...queryWithoutPage,
    page: currentPage ? parseInt(currentPage) : 1,
    attributes: true,
  }).uri;
  const productResetUri = buildProductsRequest({
    ...queryWithoutPage,
    page: undefined,
    attributes: true,
  }).uri;

  const productQueryKey = [
    CacheKey.PRODUCTS,
    'infinite',
    currentPage ?? null,
    locale,
    authToken ?? null,
    productRequestUri,
  ];

  const productsQuery = useInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale: locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [Number(currentPage ?? 1)] } : undefined,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    queryKey: productQueryKey,
    queryFn: ({ pageParam, signal }) => {
      return fetchProducts({
        query: {
          ...queryWithoutPage,
          page: pageParam,
          attributes: true,
        },
        locale,
        authToken,
        signal,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: isInitialized,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  useEffect(() => {
    if (lastPage?.meta?.total === undefined) return;
    props.onProductTotalChange?.(lastPage.meta.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  const isPending = (!isInitialized && !productsQuery.data) || productsQuery.isPending;

  return (
    <>
      {isPending && (
        <CategoryProductsSkeleton
          className={{
            items_wrapper: props.className?.grid_wrapper,
            item: props.className?.product,
          }}
        />
      )}
      <div
        className={cn(
          'relative col-span-12 flex flex-col gap-6 @container',
          'ea-category-products',
          {
            'col-span-12': !templateSettings.category?.left_filter?.enabled,
            hidden: isPending,
          },
        )}
      >
        {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}
        {!!flatList.length && (
          <CollectionPageSchema
            category={props.category}
            products={flatList}
            order={filters.sort.order ?? 'desc'}
            url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
            currency={(generalLayout?.currency_code || generalLayout?.currency)}
          />
        )}
        {props.swiper ? (
          <CategoryProductsSwiper products={flatList ?? []} />
        ) : (
          <>
            <ProductsGrid
              products={flatList ?? []}
              className={{
                wrapper: cn(
                  'grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 2xl:gap-8',
                  'ea-category-products__grid',
                  props.className?.grid_wrapper,
                ),
                product: {
                  main: cn(props.className?.product),
                },
              }}
            />

            {!!productsQuery.hasNextPage && (
              <div
                className={cn('flex flex-row justify-center', 'ea-category-products__load-more')}
              >
                <Button
                  variant="secondary"
                  isLoading={productsQuery.isFetchingNextPage}
                  onClick={() => productsQuery.fetchNextPage()}
                >
                  {t('text338')}
                </Button>
              </div>
            )}

            <div className={cn('flex flex-row justify-center', 'ea-category-products__pagination')}>
              <Pagination
                links={lastPage?.meta.links}
                onPageClicked={page => {
                  if (String(page) !== String(currentPage)) return;
                  queryClient.resetQueries({
                    queryKey: productQueryKey,
                    exact: true,
                  });
                }}
                scrollToElementAfterPageChange={
                  props.paginationProps?.scrollToElementAfterPageChange || undefined
                }
                resetWhen={[productResetUri]}
              />
            </div>

            {!!isGlobalNavigating && (
              <div
                className={cn(
                  'absolute inset-0 z-10 bg-background/30',
                  'ea-category-products__loading-overlay',
                )}
              ></div>
            )}
          </>
        )}
      </div>
    </>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('min-h-[400px] min-w-[250px]', className?.item)} />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
